import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaUserPlus, FaFileSignature, FaHeartPulse, FaCampground, FaUserGroup } from "react-icons/fa6";
import { FaWater, FaHiking } from "react-icons/fa";
import { GiMushroomGills, GiMushroomsCluster, GiSeedling, GiSunbeams, GiMountains, GiInfinity } from "react-icons/gi";
import { PiMountains } from "react-icons/pi";
import { BsTree } from "react-icons/bs";
import { VscCompass } from "react-icons/vsc";
import "./HowItWorks.css";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay } },
});

const RAIL_ICONS = [PiMountains, BsTree, FaWater, VscCompass];

/* ── Documentation accepted for Registration (matches Apply form) ── */
const ELIGIBILITY_DOC_TYPES = [
  "Government/department-issued service ID or credentials",
  "DD214/DD1300 or equivalent government service record",
  "Official employment/separation/retirement letter from the agency",
  "State professional certification/license showing the qualifying occupation, preferably with evidence of actual agency service",
  "Pension/retirement documentation",
  "Other official documentation approved by Forge after review",
];

/* ── Before the Retreat — 5 tiles ── */
const BEFORE_STEPS = [
  {
    num: "01",
    Icon: FaUserPlus,
    title: "Registration",
    teaser: "Create your account and tell us a bit about yourself.",
    body: "Sign up for the site to get started. This unlocks our resource library, free and discounted partner benefits, and the ability to begin your application when you're ready. To confirm eligibility, we'll ask for one piece of documentation showing your service or qualifying background.",
    docList: true,
  },
  {
    num: "02",
    Icon: FaFileSignature,
    title: "Application",
    teaser: "Complete the application so we can get to know you better.",
    body: "Once you're registered, you can begin the application for the retreat itself. It opens with a pre-screening covering some medical and personal questions, then moves through a few phases with questions, educational information, and videos. This material is important for you to understand the journey you are about to begin.",
  },
  {
    num: "03",
    Icon: FaHeartPulse,
    title: "Medical Clearance",
    teaser: "Provide a simple medical clearance to ensure your safety.",
    body: "Before you move forward, a doctor has to sign off on your medical clearance, and you'll speak with one of our clinical staff as part of that process. Taking medication doesn't disqualify you from the retreat — some medications affect which specific path is available to you, and if that's the case, we'll help you find the right one.",
  },
  {
    num: "04",
    Icon: FaCampground,
    title: "Placement",
    teaser: "Our team reviews your application and confirms your spot.",
    body: "Once you're cleared, you're placed in queue. Everything from here happens on your membership dashboard. When your number comes up, we'll reach out by email, text, and/or phone to let you know it's your turn. From there, you log in and choose whichever date and path works best for you.",
  },
  {
    num: "05",
    Icon: FaHiking,
    title: "Preparation",
    teaser: "Get ready for the journey with pre-retreat resources and guidance.",
    body: "This is where the actual preparation happens. You'll have periodic calls, both one-on-one and with your cohort, along with ongoing work to keep up with: videos, short readings, and other material. All of it prepares your body and mind for what's ahead. As your retreat date gets closer, things pick up, with more meetings and more specific guidance.",
  },
];

const RETREAT_TAGS = [
  { Icon: GiMushroomGills, label: "Ayahuasca" },
  { Icon: GiMushroomsCluster, label: "Mushroom" },
  { Icon: GiSeedling, label: "Abstinence" },
  { Icon: GiSunbeams, label: "Ceremony" },
  { Icon: GiMountains, label: "Recovery" },
  { Icon: GiInfinity, label: "Everything\naround it" },
];

const RETREAT_BODY =
  "Once you arrive in Colombia, you begin the retreat itself. Depending on your path, this includes an ayahuasca ceremony, a mushroom ceremony, or the abstinence track for those not doing medicine. Each ceremony is followed by recovery time, with support to help you process what came up. Alongside the ceremony work, you'll take part in group and individual sessions, short excursions, recreational activities, and time to relax on our partnered 32-acre eco-luxury resort. Medical staff are on site for the entire retreat.";

const REINTEGRATION_BODY = "Reintegration includes individual and group sessions, giving you continued support as you settle back into day-to-day life.";

/* ── Small "before" tile ── */
function BeforeTile({ step, expanded, onToggle, delay }) {
  const { Icon } = step;
  return (
    <motion.div className={`hiw__tile ${expanded ? "hiw__tile--open" : ""}`} variants={fadeUp(delay)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
      <span className="hiw__tile-tag">{step.num}</span>
      <button type="button" className="hiw__tile-inner" onClick={onToggle} aria-expanded={expanded}>
        <span className="hiw__tile-icon">
          <Icon aria-hidden="true" />
        </span>
        <span className="hiw__tile-title">{step.title}</span>
        <span className="hiw__tile-teaser">{step.teaser}</span>
        <span className="hiw__tile-more">
          {expanded ? "Show less" : "Learn more"} <FaChevronDown className={`hiw__tile-chevron ${expanded ? "hiw__tile-chevron--flip" : ""}`} aria-hidden="true" />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div className="hiw__tile-panel" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease }}>
            <p className="hiw__tile-body">{step.body}</p>
            {step.docList && (
              <ul className="hiw__doc-list">
                {ELIGIBILITY_DOC_TYPES.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main component ────────────────────────────────────── */
export default function HowItWorks() {
  const [openBefore, setOpenBefore] = useState(null);
  const [retreatOpen, setRetreatOpen] = useState(false);
  const [reintegrationOpen, setReintegrationOpen] = useState(false);

  return (
    <section className="hiw" id="retreat">
      <div className="hiw__layout container-fg container-2xxl">
        <motion.aside className="hero__rail hiw__rail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5, ease }} aria-hidden="true">
          <span className="hero__rail-num">03</span>
          <span className="hero__rail-line" />
          <ul className="hero__rail-icons">
            {RAIL_ICONS.map((Icon, i) => (
              <li key={i}>
                <Icon />
                <span className={`hero__rail-line ${i === RAIL_ICONS.length - 1 ? "" : "alt"}`} />
              </li>
            ))}
          </ul>
        </motion.aside>

        <motion.div variants={fadeUp(0)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
          <span className="hiw__eyebrow u-eyebrow">The Process</span>
          <h2 className="hiw__headline u-display">How It Works</h2>
          <p className="hiw__intro">Forge is a journey with purpose. Every step is designed to prepare you for transformation.</p>
        </motion.div>

        {/* ── Before the Retreat ── */}
        <motion.div className="hiw__tier-rule" variants={fadeUp(0.05)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
          <span className="hiw__tier-label">Before the Retreat</span>
          <span className="hiw__tier-line" />
          <span className="hiw__tier-dot" aria-hidden="true" />
        </motion.div>

        <div className="hiw__before-grid">
          {BEFORE_STEPS.map((step, i) => (
            <BeforeTile key={step.num} step={step} expanded={openBefore === step.num} onToggle={() => setOpenBefore((cur) => (cur === step.num ? null : step.num))} delay={0.05 + i * 0.05} />
          ))}
        </div>

        {/* ── The Retreat ── */}
        <motion.div className="hiw__hero-tile" variants={fadeUp(0.1)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <div className="hiw__hero-left">
            <span className="hiw__hero-eyebrow">[ The Retreat ]</span>
            <h3 className="hiw__hero-title u-display">The Retreat</h3>
            <span className="hiw__hero-squiggle" aria-hidden="true">
              〜〜〜
            </span>
            <p className="hiw__hero-teaser">7 days of immersive healing, brotherhood, and growth in the heart of nature.</p>

            <button type="button" className="hiw__hero-toggle" onClick={() => setRetreatOpen((v) => !v)} aria-expanded={retreatOpen}>
              {retreatOpen ? "Show less" : "Read more"} <FaChevronDown className={`hiw__tile-chevron ${retreatOpen ? "hiw__tile-chevron--flip" : ""}`} aria-hidden="true" />
            </button>
            <AnimatePresence>
              {retreatOpen && (
                <motion.p className="hiw__hero-full" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease }}>
                  {RETREAT_BODY}
                </motion.p>
              )}
            </AnimatePresence>

            <div className="hiw__hero-tags">
              {RETREAT_TAGS.map(({ Icon, label }) => (
                <div key={label} className="hiw__hero-tag">
                  <Icon aria-hidden="true" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hiw__hero-right">
            <img src="./assets/retreats/1.jpg" alt="Evening ceremony gathering at the Forge retreat in Colombia" className="hiw__hero-img" loading="lazy" />
          </div>
        </motion.div>

        {/* ── After the Retreat ── */}
        <motion.div className="hiw__tier-rule" variants={fadeUp(0.1)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
          <span className="hiw__tier-label">After the Retreat</span>
          <span className="hiw__tier-line" />
          <span className="hiw__tier-dot" aria-hidden="true" />
        </motion.div>

        <motion.button
          type="button"
          className={`hiw__after-tile ${reintegrationOpen ? "hiw__after-tile--open" : ""}`}
          onClick={() => setReintegrationOpen((v) => !v)}
          aria-expanded={reintegrationOpen}
          variants={fadeUp(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
        >
          <span className="hiw__after-num">06</span>
          <span className="hiw__after-icon">
            <FaUserGroup aria-hidden="true" />
          </span>
          <span className="hiw__after-body">
            <span className="hiw__after-title">Reintegration</span>
            <span className="hiw__after-teaser">Continued support through individual and group sessions to help you integrate and thrive.</span>
            <AnimatePresence>
              {reintegrationOpen && (
                <motion.span className="hiw__after-full" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease }}>
                  {REINTEGRATION_BODY}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
          <FaChevronDown className={`hiw__tile-chevron ${reintegrationOpen ? "hiw__tile-chevron--flip" : ""}`} aria-hidden="true" />
          <span className="hiw__after-botanical" aria-hidden="true" />
        </motion.button>
      </div>
    </section>
  );
}
